import { ThemeEvents } from '@theme/events';

/** SessionStorage key for persisting active size system across variant changes */
const ACTIVE_SYSTEM_STORAGE_KEY = 'size-converter-active-system';

/**
 * Size Converter custom element that handles conversion between different size systems (US, UK, EU, CM).
 *
 * @extends {HTMLElement}
 */
class SizeConverter extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  get buttons() {
    return Array.from(this.querySelectorAll('.size-conversion__button'));
  }

  /** @type {HTMLElement|null} */
  get dataScript() {
    return this.querySelector('#size-chart-data');
  }

  /** @type {HTMLElement|null} */
  get variantPicker() {
    return this.closest('variant-picker');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get variantOptions() {
    const fieldset = this.#getSizeFieldset();
    return fieldset ? fieldset.querySelectorAll('.variant-option__button-label') : [];
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  /**
   * Holds the parsed size chart data. Two supported shapes:
   *  - Map keyed by option_value.id: `{ [ovid: string]: Record<string, string> }`
   *  - Legacy array of rows: `Array<Record<string, string>>`
   * @type {Record<string, Record<string, string>> | Array<Record<string, string>> | null}
   */
  #sizeChartData = null;

  /** @type {string|null} */
  #activeSystem = null;

  /** @type {string|null} */
  #baseSystem = null;

  /** @type {Map<HTMLInputElement, string>} */
  #originalSizes = new Map();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#loadSizeChartData();
    this.#storeOriginalSizes();

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.#handleButtonClick, { signal });
    });

    const picker = this.variantPicker;
    if (picker) {
      picker.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    }

    // variantDomUpdated is dispatched from variant-picker but also from ari-option-gallery-link (outside the picker).
    // Listening on document catches both; ignore updates that belong to another <main> (e.g. quick-add).
    document.addEventListener(ThemeEvents.variantDomUpdated, this.#handleVariantDomUpdated, { signal });

    if (this.#activeSystem) {
      this.#convertAllSizes();
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Persists current #activeSystem before variant change (e.g. before morph replaces DOM).
   */
  #handleVariantSelected = () => {
    if (this.#activeSystem) {
      try {
        sessionStorage.setItem(ACTIVE_SYSTEM_STORAGE_KEY, this.#activeSystem);
      } catch (_) {}
    }
  };

  /**
   * After variant picker DOM has finished updating (morph complete): restore active system
   * from sessionStorage and re-apply size conversion to the new option labels.
   * @param {Event} event
   */
  #handleVariantDomUpdated = (event) => {
    if (!this.isConnected) return;

    const myMain = this.closest('main');
    const target = event.target;
    const eventMain = target instanceof Element ? target.closest('main') : null;
    if (myMain && eventMain && myMain !== eventMain) return;

    this.#originalSizes.clear();
    this.#loadSizeChartData();
    this.#storeOriginalSizes();
    if (this.#activeSystem) {
      this.#convertAllSizes();
    }
  };

  /**
   * Loads size chart data from the embedded JSON script.
   * Restores #activeSystem from sessionStorage when present so it persists across variant changes.
   */
  #loadSizeChartData() {
    const script = this.dataScript;
    if (!script) return;

    try {
      this.#sizeChartData = JSON.parse(script.textContent || '');
      
      const activeButton = this.querySelector('.size-conversion__button--active');
      if (activeButton instanceof HTMLButtonElement) {
        this.#baseSystem = activeButton.dataset.sizeSystem?.toUpperCase() || null;
      }

      let restoredSystem = null;
      try {
        restoredSystem = sessionStorage.getItem(ACTIVE_SYSTEM_STORAGE_KEY);
      } catch (_) {}

      const validSystems = this.buttons
        .map((b) => b.dataset.sizeSystem?.toUpperCase())
        .filter(Boolean);
      if (restoredSystem && validSystems.includes(restoredSystem)) {
        this.#activeSystem = restoredSystem;
        this.buttons.forEach((button) => {
          const isActive = button.dataset.sizeSystem?.toUpperCase() === restoredSystem;
          button.classList.toggle('size-conversion__button--active', isActive);
        });
      } else {
        this.#activeSystem = this.#baseSystem;
      }
    } catch (error) {
      console.error('Failed to parse size chart data:', error);
    }
  }

  /**
   * Handles button click events.
   * @param {Event} event - The click event.
   */
  #handleButtonClick = (event) => {
    event.preventDefault();
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const system = button.dataset.sizeSystem;
    if (system) {
      this.#setActiveSystem(system);
    }
  };

  /**
   * Stores original size values from variant options.
   */
  #storeOriginalSizes() {
    this.variantOptions.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      const textElement = label.querySelector('.variant-option__button-label__text');
      
      if (!(input instanceof HTMLInputElement) || !textElement) return;

      const currentText = textElement.textContent?.trim() || '';
      
      // Only store if not already stored and doesn't have system suffix
      if (!this.#originalSizes.has(input) && !currentText.match(/\s+(US|UK|EU|CM)$/i)) {
        this.#originalSizes.set(input, currentText);
      }
    });
  }

  /**
   * Sets the active size system and triggers conversion.
   * @param {string} system - The size system to activate.
   */
  #setActiveSystem(system) {
    const newSystem = system.toUpperCase();

    // Set base system on first interaction if not set
    if (!this.#baseSystem) {
      const activeButton = this.querySelector('.size-conversion__button--active');
      if (activeButton instanceof HTMLButtonElement) {
        this.#baseSystem = activeButton.dataset.sizeSystem?.toUpperCase() || null;
      }
    }
    
    this.#activeSystem = newSystem;
    // Update button states
    this.buttons.forEach((button) => {
      const isActive = button.dataset.sizeSystem === system;
      button.classList.toggle('size-conversion__button--active', isActive);
    });
    this.#convertAllSizes();
  }

  /**
   * Converts all variant sizes to the active system.
   *
   * Supports two data shapes:
   *  - New (preferred): `{ "<option_value_id>": {US, UK, EU, CM}, ... }` — direct
   *    lookup via input.dataset.optionValueId. No parsing, no base-system guessing.
   *  - Legacy: `[{US, UK, EU, CM}, ...]` — parses option label text, matches against
   *    base system column.
   */
  #convertAllSizes() {
    if (!this.#sizeChartData || !this.#activeSystem) return;

    const targetKey = this.#activeSystem.toUpperCase();
    const data = this.#sizeChartData;
    const sizeMap = data && !Array.isArray(data) ? /** @type {Record<string, Record<string, string>>} */ (data) : null;
    /** @type {Array<Record<string, string>>} */
    const sizesArray = !sizeMap
      ? Array.isArray(data)
        ? data
        : []
      : [];

    this.variantOptions.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      const textElement = label.querySelector('.variant-option__button-label__text');

      if (!(input instanceof HTMLInputElement) || !textElement) return;

      /** @type {string | null | undefined} */
      let newValue = null;

      if (sizeMap) {
        const ovid = input.dataset.optionValueId;
        const row = ovid ? sizeMap[ovid] : null;
        if (row) newValue = row[targetKey];
      } else if (sizesArray.length && this.#baseSystem) {
        const originalSize = this.#originalSizes.get(input) || textElement.textContent?.trim() || '';
        const sizeValue = this.#parseSizeValue(originalSize);
        if (sizeValue !== null) {
          newValue = this.#convertSize(sizeValue, this.#activeSystem, this.#baseSystem, sizesArray);
        }
      }

      if (newValue !== null && newValue !== undefined && newValue !== '') {
        textElement.textContent = String(newValue);
        return;
      }

      // No value for this system on this option — restore the original label.
      const original = this.#originalSizes.get(input);
      if (original) textElement.textContent = original;
    });
  }

  /**
   * Gets the Size fieldset from the variant picker.
   * @returns {HTMLFieldSetElement|null}
   */
  #getSizeFieldset() {
    const picker = this.variantPicker;
    if (!picker) return null;

    return Array.from(picker.querySelectorAll('fieldset')).find((fieldset) => {
      const legend = fieldset.querySelector('legend');
      return legend && legend.textContent?.trim().includes(this.dataset.title || 'Size');
    }) || null;
  }

  /**
   * Parses a size value from a string.
   * @param {string} sizeString - The size string to parse.
   * @returns {number|null}
   */
  #parseSizeValue(sizeString) {
    const cleaned = sizeString.replace(/\s+(US|UK|EU|CM)$/i, '').trim();
    const match = cleaned.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Converts a size value from one system to another.
   * @param {number} sizeValue - The size value to convert.
   * @param {string} targetSystem - The target size system.
   * @param {string} baseSystem - The base size system.
   * @param {Object[]} sizesArray - The size chart data.
   * @returns {string|null}
   */
  #convertSize(sizeValue, targetSystem, baseSystem, sizesArray) {
    if (!sizesArray?.length) return null;

    const normalizedValue = String(sizeValue);
    const baseKey = baseSystem.toUpperCase();
    const targetKey = targetSystem.toUpperCase();

    const matchedEntry = sizesArray.find((entry) => {
      return String(entry[baseKey]) === normalizedValue;
    });

    if (!matchedEntry) return null;

    return matchedEntry[targetKey] ?? null;
  }

  /**
   * Formats a size value with its system suffix.
   * @param {string} size - The size value.
   * @param {string} system - The size system.
   * @returns {string}
   */
  #formatSize(size, system) {
    if (size === null || size === undefined) return '';
    return `${size} ${system.toUpperCase()}`;
  }
}

if (!customElements.get('size-converter')) {
  customElements.define('size-converter', SizeConverter);
}
